// Browser helpers that turn a canvas into a tidy signature PNG.
import { trimBounds } from "@/lib/sign/signature";

export type Signature = { blob: Blob; width: number; height: number };

/** Crops a canvas to its ink (plus a little padding) and saves it as PNG. */
export async function trimmedPng(
  canvas: HTMLCanvasElement,
  pad = 6,
): Promise<Signature | null> {
  const ctx = canvas.getContext("2d")!;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const b = trimBounds(data, canvas.width, canvas.height);
  if (!b) return null;
  const x = Math.max(0, b.x - pad);
  const y = Math.max(0, b.y - pad);
  const width = Math.min(canvas.width - x, b.width + pad * 2);
  const height = Math.min(canvas.height - y, b.height + pad * 2);
  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  out
    .getContext("2d")!
    .drawImage(canvas, x, y, width, height, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((r) =>
    out.toBlob(r, "image/png"),
  );
  return blob ? { blob, width, height } : null;
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** A data URL back to a Blob, without any network request. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [head, body] = dataUrl.split(",");
  const type = head.match(/data:([^;]+)/)?.[1] ?? "image/png";
  const bytes = Uint8Array.from(atob(body), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type });
}
